import React from 'react';
import { Box, Button, Card, Divider, Grid, TextField, Tooltip, Typography } from '@mui/material';
import { usePayappProcess } from '../../context/PayappProcessContext';
import {
  coverPageFields,
  getDisplayLabel,
  isPrimaryLabel,
  normalizeValue,
  payappProcessTooltips,
  splitIntoChunks,
} from '../file-upload/Payapp.util';
import { parsePeriodDateToISO } from '../../common/utils/common.util';
import NumericFormatInput from '../general/NumericFormatInput';
import { Help, InfoOutlined, Warning } from '@mui/icons-material';
import DynamicDatePicker from '../common/DynamicDatePicker';
import { format, isValid, parse } from 'date-fns';

const columns = splitIntoChunks(coverPageFields, 2);

const CoverPageVerification: React.FC = () => {
  const { state, updateCoverPageValue } = usePayappProcess();
  const { coverPageValues, projectDraw } = state;
  const [focusedField, setFocusedField] = React.useState<string | null>(null);

  const handleFieldChange = (field: string, value: string) => {
    updateCoverPageValue(field, value);
  };

  const renderField = (label: string, index: number, isPrimary: boolean, isLast = false) => {
    let editableRawValue = coverPageValues[label] ?? '';

    // Special handling for Application Number - treat as string, not currency
    if (label === 'Application Number') {
      const formattedEditableValue = focusedField === label ? editableRawValue.toString() : editableRawValue.toString();

      // Get previous month application number from projectDraw
      const previousAppNumber = projectDraw?.projectDrawSummary?.applicationNumber;

      // Determine which icon to show and what message
      let iconToShow = null;
      let tooltipMessage = '';

      if (previousAppNumber !== undefined && previousAppNumber !== null) {
        const currentAppNumber = parseInt(String(editableRawValue)) || 0;
        const previousAppNumberInt = parseInt(String(previousAppNumber)) || 0;
        const difference = currentAppNumber - previousAppNumberInt;

        tooltipMessage = ` Previous Month: <strong>${previousAppNumber}</strong> `;

        // Check if difference is negative or greater than 1
        if (difference < 0 || difference > 1) {
          iconToShow = <Warning sx={{ fontSize: 16, color: 'orange', ml: 0.5 }} />;
        } else {
          iconToShow = <InfoOutlined sx={{ fontSize: 16, color: 'rgb(171 170 170)', ml: 0.5 }} />;
        }
      }

      return (
        <React.Fragment key={label}>
          <Box display='flex' flexDirection='column' gap={0.5} mb={0}>
            <Box display='flex' justifyContent='space-between' alignItems='center'>
              <Box display='flex' alignItems='center'>
                <Typography variant='subtitle2' fontWeight={isPrimary ? 600 : 400} sx={{ flex: 1 }}>
                  {label}
                </Typography>
                {iconToShow && (
                  <Tooltip
                    title={
                      <Box>
                        <Typography variant='inherit'>
                          Previous Month: <strong>{previousAppNumber}</strong>
                        </Typography>
                      </Box>
                    }
                    arrow
                  >
                    {iconToShow}
                  </Tooltip>
                )}
              </Box>
              <TextField
                variant='outlined'
                size='medium'
                value={formattedEditableValue}
                onChange={e => handleFieldChange(label, e.target.value)}
                onFocus={() => setFocusedField(label)}
                onBlur={() => {
                  handleFieldChange(label, editableRawValue.toString());
                  setFocusedField(null);
                }}
                sx={{
                  width: '160px',
                  input: {
                    fontSize: '1rem',
                    textAlign: 'right',
                    paddingRight: '8px',
                    paddingTop: '5px',
                    paddingBottom: '5px',
                  },
                }}
              />
            </Box>
          </Box>
          {!isLast && <Divider />}
        </React.Fragment>
      );
    }

    // Period date fields — date picker
    if (label === 'Period Beginning' || label === 'Period Ending') {
      const prevPeriodEnding = projectDraw?.projectDrawSummary?.periodEnding;

      const handleAutofill = () => {
        if (!prevPeriodEnding) return;
        try {
          const isoEnd = parsePeriodDateToISO(prevPeriodEnding);
          if (!isoEnd) return;
          const end = parse(isoEnd, 'yyyy-MM-dd', new Date());
          if (!isValid(end)) return;
          const begin = new Date(end);
          begin.setDate(begin.getDate() + 1);
          handleFieldChange(label, format(begin, 'yyyy-MM-dd'));
        } catch {
          // ignore unparseable dates
        }
      };

      return (
        <React.Fragment key={label}>
          <Box display='flex' flexDirection='column' gap={0.5} mb={0}>
            <Box display='flex' justifyContent='space-between' alignItems='center'>
              <Box display='flex' alignItems='center' gap={0.5}>
                <Typography variant='subtitle2' fontWeight={isPrimary ? 600 : 400} sx={{ flex: 1 }}>
                  {label}
                </Typography>
                {label === 'Period Beginning' && prevPeriodEnding && (
                  <Tooltip title='Set to previous Period Ending + 1 day' arrow>
                    <Button
                      size='small'
                      variant='text'
                      onClick={handleAutofill}
                      sx={{ fontSize: '0.7rem', py: 0, px: 0.5, textTransform: 'none', whiteSpace: 'nowrap' }}
                    >
                      Autofill from last month
                    </Button>
                  </Tooltip>
                )}
              </Box>
              <Box
                sx={{
                  width: '160px',
                  '& .MuiInputBase-input': {
                    fontSize: '1rem !important',
                    paddingTop: '5px !important',
                    paddingBottom: '5px !important',
                    paddingRight: '4px !important',
                  },
                  '& .MuiInputAdornment-root': { ml: 0 },
                }}
              >
                <DynamicDatePicker
                  fieldName={label}
                  value={parsePeriodDateToISO(editableRawValue.toString())}
                  onChange={(field, val) => handleFieldChange(field, val ?? '')}
                />
              </Box>
            </Box>
          </Box>
          {!isLast && <Divider />}
        </React.Fragment>
      );
    }

    // Ensure 'Total Retainage Held' always has negative value for display
    if (label === 'Total Retainage Held') {
      const normalized = normalizeValue(editableRawValue.toString());
      if (!normalized.startsWith('-')) {
        editableRawValue = `-${normalized}`;
      }
    }

    const formattedEditableValue = !editableRawValue
      ? ''
      : label === '%'
        ? editableRawValue.toString().replace(/\s*%$/, '').trim()
        : normalizeValue(editableRawValue.toString());

    const handleBlur = () => {
      let cleaned = normalizeValue(editableRawValue.toString() || '');

      // Again, ensure negative sign when saving 'Total Retainage Held'
      if (label === 'Total Retainage Held' && !cleaned.startsWith('-')) {
        cleaned = `-${cleaned}`;
      }

      handleFieldChange(label, cleaned);
    };

    return (
      <React.Fragment key={label}>
        <Box display='flex' flexDirection='column' gap={0.5} mb={0}>
          <Box display='flex' justifyContent='space-between' alignItems='center'>
            <Typography variant='subtitle2' fontWeight={isPrimary ? 600 : 400} sx={{ flex: 1 }}>
              {getDisplayLabel(label)}
            </Typography>
            <TextField
              variant='outlined'
              size='medium'
              value={formattedEditableValue}
              onChange={e => handleFieldChange(label, e.target.value)}
              onFocus={() => setFocusedField(label)}
              onBlur={() => {
                handleBlur();
                setFocusedField(null);
              }}
              sx={{
                width: '160px',
                input: {
                  fontSize: '1rem',
                  textAlign: 'right',
                  paddingRight: label === '%' && focusedField !== label ? '0px' : '8px',
                  paddingTop: '5px',
                  paddingBottom: '5px',
                },
              }}
              InputProps={{
                inputComponent: NumericFormatInput as any,
                startAdornment:
                  label !== '%' ? (
                    <Typography variant='caption' sx={{ mr: 0.5 }}>
                      $
                    </Typography>
                  ) : undefined,
                endAdornment:
                  label === '%' ? (
                    <Typography variant='caption' sx={{ ml: 0.5 }}>
                      %
                    </Typography>
                  ) : undefined,
              }}
            />
          </Box>
        </Box>
        {!isLast && <Divider />}
      </React.Fragment>
    );
  };

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid size={{ xl: 12 }} key={`row-0`} minHeight={'100%'}>
          <Box display='flex' alignItems='center'>
            <Typography variant='h5' color='primary' sx={{ ml: 0.5 }}>
              Verify Cover Page
            </Typography>
            <Tooltip
              title={payappProcessTooltips.VerifyCoverPage}
              placement='bottom'
              arrow={false}
              slotProps={{
                tooltip: {
                  sx: {
                    padding: '12px 16px',
                    textAlign: 'left',
                    maxWidth: 'none',
                  },
                },
              }}
            >
              <Help sx={{ ml: 1, cursor: 'pointer', fontSize: '20px' }} />
            </Tooltip>
          </Box>
        </Grid>
        {columns.map((columnFields, colIndex) => (
          <Grid size={{ xl: 6 }} key={`column-${colIndex}`} minHeight={'100%'}>
            <Card elevation={colIndex === 0 ? 1 : 3} style={{ padding: '16px', height: '100%' }}>
              {columnFields.map((field, fieldIndex) =>
                renderField(field, fieldIndex, isPrimaryLabel(field), fieldIndex === columnFields.length - 1),
              )}
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default CoverPageVerification;
